import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';
import { RouteTracker } from './components/RouteTracker';
import { CheckoutResultHandler } from './components/CheckoutResultHandler';
import { RetailorBackgroundToast } from './components/RetailorBackgroundToast';
import { useAuth } from './hooks/useAuth';
import './App.css';

// Everything except the landing page loads on demand.
//
// Measured before this: a visitor who opened draftresume.app and read the
// headline downloaded 213KB gzipped of JavaScript - the whole builder wizard,
// every resume template, the editors, sign-in, the account page and pdf.js -
// because every route was a static import. None of it can paint the hero, and
// all of it has to parse before React renders, which is why phones recorded a
// 3.4s largest-contentful-paint on a page whose HTML arrives in 219ms.
//
// The landing page stays eager: it is the thing being waited for.
const BuilderWizard = lazy(() => import('./pages/BuilderWizard').then((m) => ({ default: m.BuilderWizard })));
const SignIn = lazy(() => import('./pages/SignIn').then((m) => ({ default: m.SignIn })));
const AccountPage = lazy(() => import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })));

/**
 * Deliberately blank. These routes render in well under a second on any
 * connection that got this far, and a spinner that flashes for 200ms reads as
 * jank rather than progress.
 */
const RouteFallback = () => <div className="min-h-screen bg-gradient-to-br from-orange-50 to-stone-100" />;

function HomeOrRedirect() {
  const [params] = useSearchParams();
  const { isSignedIn, loading } = useAuth();
  const ref = params.get('ref');
  const invite = params.get('invite');

  // Wait for the session check so we don't flash-redirect a signed-in user
  // to /login just because an invite or ref param is present.
  if ((ref || invite) && loading) return null;

  // Signed-in users hitting an invite link: send them to /account with a
  // banner explaining invites apply to new accounts only. Gives them the
  // option to sign out and redeem under a fresh account.
  if (invite && isSignedIn) {
    return <Navigate to={`/account?pending_invite=${encodeURIComponent(invite)}`} replace />;
  }

  // Anonymous users with ref or invite: route through /login so the code
  // survives the OTP roundtrip and gets applied on verify.
  if ((ref || invite) && !isSignedIn) {
    const qs = new URLSearchParams();
    if (ref) qs.set('ref', ref);
    if (invite) qs.set('invite', invite);
    return <Navigate to={`/login?${qs.toString()}`} replace />;
  }

  // Signed-in users hitting a ref link: the ref only credits new signups, so
  // it is meaningless for them. Silently drop the param and show the landing.
  return <LandingPage />;
}

/**
 * Everything inside the router, with no router of its own.
 *
 * The prerender step renders this under a StaticRouter at build time so the
 * landing page ships as real HTML; the browser mounts the same tree under a
 * BrowserRouter and hydrates it. Two routers, one tree, identical markup - the
 * moment they diverge, hydration falls back to a full client render and the
 * prerender buys nothing.
 */
export function AppRoutes() {
  return (
    <>
      <RouteTracker />
      <CheckoutResultHandler />
      <RetailorBackgroundToast />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomeOrRedirect />} />
          <Route path="/app" element={<BuilderWizard />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/login" element={<SignIn />} />
          <Route path="/signup" element={<SignIn />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
